import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE, SOCKET_URL } from '../config';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const driverIcon = new L.Icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/2972/2972185.png', iconSize:[40,40], iconAnchor:[20,40] });
const pickupIcon = new L.Icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/1483/1483336.png', iconSize:[35,35], iconAnchor:[17,35] });
const dropoffIcon = new L.Icon({ iconUrl: 'https://cdn-icons-png.flaticon.com/512/484/484167.png', iconSize:[35,35], iconAnchor:[17,35] });

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 15); }, [center, map]);
  return null;
}

function RoutingLine({ from, to }) {
  const map = useMap();
  useEffect(() => {
    if (!from || !to || !from[0] || !from[1] || !to[0] || !to[1]) return;
    let line = null;
    const ctrl = new AbortController();
    fetch(`https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`, { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        if (data.routes?.[0]) {
          const coords = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
          line = L.polyline(coords, { color: '#3498db', weight: 5, opacity: 0.8 });
          line.addTo(map);
          try { map.fitBounds(line.getBounds(), { padding: [50, 50] }); } catch(e) {}
        }
      }).catch(() => {});
    return () => {
      ctrl.abort();
      if (line) { try { map.removeLayer(line); } catch(e) {} }
    };
  }, [from?.[0], from?.[1], to?.[0], to?.[1], map]);
  return null;
}

const STATUS_AR = { searching:'🔍 بحث', accepted:'✅ مقبول', in_transit:'🚀 جاري', delivered:'🎉 تم', cancelled:'❌ ملغي', failed:'😔 فشل' };
const STATUS_COLOR = { searching:'#f39c12', accepted:'#27ae60', in_transit:'#3498db', delivered:'#27ae60', cancelled:'#e74c3c', failed:'#e74c3c' };

export default function Home({ token, user, driver, onLogout }) {
  const [online, setOnline] = useState(localStorage.getItem('driver_online') === 'true');
  const [offer, setOffer] = useState(null);
  const [activeOrder, setActiveOrder] = useState(() => {
    try { const s = localStorage.getItem('activeOrder'); return s ? JSON.parse(s) : null; } catch(e) { return null; }
  });
  const [countdown, setCountdown] = useState(30);
  const [stats, setStats] = useState({ total_trips:0, avg_rating:null });
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState('home');
  const [driverPos, setDriverPos] = useState(() => {
    try { const p = localStorage.getItem('driverPos'); return p ? JSON.parse(p) : null; } catch(e) { return null; }
  });
  const socketRef = useRef(null);
  const gpsRef = useRef(null);
  const alertRef = useRef(null);

  const stopAlert = () => { if (alertRef.current) { clearInterval(alertRef.current); alertRef.current = null; } };

  const playAlert = () => {
    stopAlert();
    const beep = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [0, 0.25, 0.5, 0.75].forEach(t => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.frequency.value = t < 0.5 ? 1200 : 1500; o.type = 'square';
          g.gain.setValueAtTime(2, ctx.currentTime + t);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.2);
          o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.2);
        });
      } catch(e) {}
    };
    beep();
    alertRef.current = setInterval(beep, 1200);
  };

  const stopGPS = useCallback(() => { if (gpsRef.current) { clearInterval(gpsRef.current); gpsRef.current = null; } }, []);

  const startGPS = useCallback(() => {
    if (gpsRef.current) return;
    gpsRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(pos => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setDriverPos([lat, lng]);
        localStorage.setItem('driverPos', JSON.stringify([lat, lng]));
        if (socketRef.current) socketRef.current.emit('driver:location', { lat, lng, speed: pos.coords.speed, heading: pos.coords.heading });
      }, () => {}, { enableHighAccuracy: true });
    }, 5000);
  }, []);

  const loadOrders = useCallback(() => {
    axios.get(`${API_BASE}/orders`).then(r => setOrders(r.data.orders || [])).catch(()=>{});
  }, []);

  useEffect(() => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.get(`${API_BASE}/drivers/me`).then(r => {
      const isOnline = r.data.driver.availability === 'online';
      setOnline(isOnline);
      localStorage.setItem('driver_online', String(isOnline));
      setStats({ total_trips: r.data.driver.total_trips||0, avg_rating: r.data.driver.avg_rating });
      if (isOnline || localStorage.getItem('activeOrder')) startGPS();
    }).catch(()=>{});
    loadOrders();
  }, [token, startGPS, loadOrders]);

  useEffect(() => {
    if (!token) return;
    const socket = io(SOCKET_URL, { auth: { token }, reconnection: true, reconnectionAttempts: Infinity, reconnectionDelay: 1000, timeout: 20000, pingTimeout: 60000, pingInterval: 25000 });
    socketRef.current = socket;
    socket.on('order:cancelled', (data) => {
      const cancellerName = data.cancellerName || (data.cancelledBy === 'client' ? 'العميل' : 'الطيار');
      alert('تم إلغاء الطلب بواسطة: ' + cancellerName);
      setActiveOrder(null); setOffer(null); stopAlert();
      localStorage.removeItem('activeOrder');
      loadOrders();
    });
    socket.on('order:offer', (data) => { playAlert(); setOffer(data); setCountdown(data.timeoutSec || 30); });
    return () => { socket.disconnect(); stopGPS(); stopAlert(); };
  }, [token, stopGPS, loadOrders]);

  useEffect(() => {
    if (!offer) return;
    const t = setInterval(() => setCountdown(c => { if (c <= 1) { clearInterval(t); setOffer(null); stopAlert(); return 0; } return c - 1; }), 1000);
    return () => clearInterval(t);
  }, [offer]);

  const toggleOnline = async () => {
    const newStatus = online ? 'offline' : 'online';
    try {
      await axios.patch(`${API_BASE}/drivers/me/availability`, { availability: newStatus });
      setOnline(!online);
      localStorage.setItem('driver_online', String(newStatus === 'online'));
      if (newStatus === 'online') startGPS(); else stopGPS();
    } catch(err) { alert('حصل خطأ'); }
  };

  const respondOffer = (response) => {
    if (!offer || !socketRef.current) return;
    stopAlert();
    socketRef.current.emit('order:respond', { offerId: offer.offerId, response });
    if (response === 'accepted') {
      const o = { ...offer, status: 'accepted' };
      setActiveOrder(o);
      localStorage.setItem('activeOrder', JSON.stringify(o));
    }
    setOffer(null);
  };

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${API_BASE}/orders/${activeOrder.orderId}/status`, { status });
      if (status === 'delivered' || status === 'failed') {
        setActiveOrder(null); localStorage.removeItem('activeOrder'); loadOrders();
      } else {
        const o = { ...activeOrder, status };
        setActiveOrder(o); localStorage.setItem('activeOrder', JSON.stringify(o));
      }
    } catch(err) { alert('حصل خطأ'); }
  };

  const cancelOrder = async () => {
    if (!window.confirm('هل تريد إلغاء الطلب؟')) return;
    try {
      await axios.patch(`${API_BASE}/orders/${activeOrder.orderId}/driver-cancel`);
      setActiveOrder(null); localStorage.removeItem('activeOrder'); loadOrders();
    } catch(err) { alert(err.response?.data?.error || 'لا يمكن إلغاء الطلب'); }
  };

  if (driver?.status === 'pending') return (
    <div style={S.pendingPage}>
      <div style={S.pendingIcon}>⏳</div>
      <h2 style={S.pendingTitle}>طلبك قيد المراجعة</h2>
      <p style={S.pendingSub}>سيتم مراجعة بياناتك من الإدارة</p>
      <button onClick={onLogout} style={S.logoutBtn}>تسجيل الخروج</button>
    </div>
  );

  const headerBg = online ? 'linear-gradient(135deg,#11998e,#38ef7d)' : 'linear-gradient(135deg,#10b981,#059669)';
  const mapCenter = driverPos || (activeOrder?.pickupLat ? [activeOrder.pickupLat, activeOrder.pickupLng] : [30.0444, 31.2357]);
  const routeTo = activeOrder?.status === 'in_transit'
    ? (activeOrder.dropoffLat ? [activeOrder.dropoffLat, activeOrder.dropoffLng] : null)
    : (activeOrder?.pickupLat ? [activeOrder.pickupLat, activeOrder.pickupLng] : null);

  return (
    <div style={S.page}>
      <div style={{...S.header, background: headerBg}}>
        <div style={S.headerTop}>
          <button onClick={onLogout} style={S.logoutSmall}>خروج</button>
          <div style={S.headerInfo}>
            <span style={S.headerName}>{user?.full_name}</span>
            <span style={S.headerCompany}>{driver?.company_name}</span>
          </div>
          <div style={S.avatar}>🛵</div>
        </div>
        <div style={S.statusRow}>
          <div style={S.statusDot(online)}/>
          <span style={S.statusText}>{online ? 'متاح للعمل' : 'غير متاح'}</span>
        </div>
        <button onClick={toggleOnline} style={{...S.toggleBtn, ...(online?S.toggleOn:S.toggleOff)}}>
          {online ? '⏸ إيقاف العمل' : '▶ ابدأ العمل'}
        </button>
      </div>

      <div style={S.statsRow}>
        {[['🚀', stats.total_trips, 'رحلة'],['⭐', stats.avg_rating||'—', 'تقييم']].map(([ic,v,lb])=>(
          <div key={lb} style={S.stat}><span style={S.statNum}>{ic} {v}</span><span style={S.statLabel}>{lb}</span></div>
        ))}
      </div>

      <div style={S.tabs}>
        {[['home','🏠','الرئيسية'],['map','🗺️','الخريطة'],['orders','📋','رحلاتي']].map(([id,ic,lb])=>(
          <button key={id} style={{...S.tab, ...(tab===id?S.tabActive:{})}} onClick={()=>setTab(id)}>{ic} {lb}</button>
        ))}
      </div>

      {tab === 'home' && (
        <div>
          {!online && !activeOrder && <div style={S.centerMsg}><p style={{fontSize:'48px'}}>😴</p><p style={{color:'#888'}}>اضغط "ابدأ العمل" لاستقبال الطلبات</p></div>}
          {online && !offer && !activeOrder && <div style={S.centerMsg}><p style={{fontSize:'48px'}}>🔍</p><p style={{color:'#888'}}>في انتظار طلبات...</p></div>}
          {activeOrder && (
            <div style={S.activeCard}>
              <h3 style={S.activeTitle}>🚀 رحلة جارية</h3>
              <div style={S.activeDetail}>
                <div style={S.activeRow}><span>📍</span><div><span>من: {activeOrder.pickupAddress}</span>{activeOrder.pickupNotes&&<div style={{fontSize:"12px",color:"#888",marginTop:"2px"}}>📝 {activeOrder.pickupNotes}</div>}</div></div>
                <div style={S.activeRow}><span>🏁</span><div><span>إلى: {activeOrder.dropoffAddress}</span>{activeOrder.dropoffNotes&&<div style={{fontSize:"12px",color:"#888",marginTop:"2px"}}>📝 {activeOrder.dropoffNotes}</div>}</div></div>
                {activeOrder.senderPhone&&<div style={S.activeRow}><span>📞</span><a href={"tel:"+activeOrder.senderPhone} style={{color:"#10b981",textDecoration:"none"}}>المرسل: {activeOrder.senderPhone}</a></div>}
                {activeOrder.receiverPhone&&<div style={S.activeRow}><span>📞</span><a href={"tel:"+activeOrder.receiverPhone} style={{color:"#10b981",textDecoration:"none"}}>المرسل إليه: {activeOrder.receiverPhone}</a></div>}
                <div style={S.activeRow}><span>💰</span><span>السعر: {activeOrder.price} جنيه</span></div>
              </div>
              <div style={S.actionBtns}>
                {activeOrder.status !== 'in_transit' && <button onClick={()=>updateStatus('in_transit')} style={S.actionBtn('#3498db')}>📦 استلمت الطلب</button>}
                <button onClick={()=>updateStatus('delivered')} style={S.actionBtn('#27ae60')}>✅ تم التوصيل</button>
                <button onClick={()=>updateStatus('failed')} style={S.actionBtn('#e67e22')}>❌ فشل التوصيل</button>
                <button onClick={cancelOrder} style={S.actionBtn('#c0392b')}>🚫 إلغاء الطلب</button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'map' && (
        <div style={{height:'calc(100vh - 200px)'}}>
          <MapContainer center={mapCenter} zoom={14} style={{height:'100%',width:'100%'}}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
            {driverPos && <MapUpdater center={driverPos}/>}
            {driverPos && <Marker position={driverPos} icon={driverIcon}><Popup>أنت هنا</Popup></Marker>}
            {activeOrder?.pickupLat && <Marker position={[activeOrder.pickupLat, activeOrder.pickupLng]} icon={pickupIcon}><Popup>📍 نقطة الاستلام</Popup></Marker>}
            {activeOrder?.dropoffLat && <Marker position={[activeOrder.dropoffLat, activeOrder.dropoffLng]} icon={dropoffIcon}><Popup>🏁 نقطة التسليم</Popup></Marker>}
            {driverPos && routeTo && <RoutingLine from={driverPos} to={routeTo}/>}
          </MapContainer>
        </div>
      )}

      {tab === 'orders' && (
        <div style={{padding:'16px'}}>
          <h3 style={{color:'#1a1a2e', margin:'0 0 16px'}}>سجل الرحلات ({orders.length})</h3>
          {orders.length === 0
            ? <div style={S.centerMsg}><p style={{fontSize:'48px'}}>📋</p><p style={{color:'#888'}}>لا توجد رحلات بعد</p></div>
            : orders.map(o => (
              <div key={o.id} style={S.orderCard}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'8px'}}>
                  <span style={{...S.badge, background:(STATUS_COLOR[o.status]||'#888')+'22', color:STATUS_COLOR[o.status]||'#888'}}>{STATUS_AR[o.status]||o.status}</span>
                  <span style={{fontSize:'12px',color:'#888'}}>{new Date(o.requested_at).toLocaleDateString('ar-EG')}</span>
                </div>
                <p style={{margin:'4px 0',fontSize:'13px',color:'#555'}}>📍 {o.pickup_address}{o.pickup_notes && <span style={{display:'block',fontSize:'12px',color:'#888',marginTop:'2px'}}>📝 {o.pickup_notes}</span>}</p>
                <p style={{margin:'4px 0',fontSize:'13px',color:'#555'}}>🏁 {o.dropoff_address}{o.dropoff_notes && <span style={{display:'block',fontSize:'12px',color:'#888',marginTop:'2px'}}>📝 {o.dropoff_notes}</span>}</p>
                <p style={{margin:'4px 0',fontSize:'13px',fontWeight:'bold',color:'#27ae60'}}>💰 {o.base_price} جنيه</p>
                {o.status==='cancelled' && o.cancelled_by_name && <p style={{margin:'4px 0',fontSize:'12px',color:'#e74c3c'}}>ملغي بواسطة: {o.cancelled_by_name}</p>}
              </div>
            ))
          }
        </div>
      )}

      {offer && (
        <div style={S.offerOverlay}>
          <div style={S.offerCard}>
            <div style={S.offerTimer(countdown)}>
              <span style={S.offerTimerNum}>{countdown}</span>
              <span style={S.offerTimerLabel}>ثانية</span>
            </div>
            <h3 style={S.offerTitle}>🚀 طلب توصيل جديد!</h3>
            <div style={S.offerDetail}>
              <div style={S.offerRow}><span style={S.offerIcon}>📍</span><div><div style={S.offerDLabel}>من</div><div style={S.offerDVal}>{offer.pickupAddress}</div>{offer.pickupNotes&&<div style={{fontSize:'12px',color:'#888',marginTop:'2px'}}>📝 {offer.pickupNotes}</div>}</div></div>
              <div style={S.offerDivider}/>
              <div style={S.offerRow}><span style={S.offerIcon}>🏁</span><div><div style={S.offerDLabel}>إلى</div><div style={S.offerDVal}>{offer.dropoffAddress}</div>{offer.dropoffNotes&&<div style={{fontSize:'12px',color:'#888',marginTop:'2px'}}>📝 {offer.dropoffNotes}</div>}{offer.senderPhone&&<div style={{fontSize:'12px',color:'#3b82f6',marginTop:'4px'}}>📞 المرسل: {offer.senderPhone}</div>}{offer.receiverPhone&&<div style={{fontSize:'12px',color:'#3b82f6',marginTop:'4px'}}>📞 المرسل إليه: {offer.receiverPhone}</div>}</div></div>
            </div>
            <p style={S.offerPrice}>💰 {offer.price} جنيه</p>
            <div style={S.offerBtns}>
              <button onClick={()=>respondOffer('rejected')} style={S.rejectBtn}>❌ رفض</button>
              <button onClick={()=>respondOffer('accepted')} style={S.acceptBtn}>✅ قبول</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  page:        { minHeight:'100vh', background:'#f0f2f5', direction:'rtl', paddingBottom:'20px' },
  header:      { padding:'20px', paddingTop:'40px', transition:'background 0.5s' },
  headerTop:   { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px' },
  headerInfo:  { textAlign:'center' },
  headerName:  { display:'block', color:'#fff', fontWeight:'bold', fontSize:'18px' },
  headerCompany: { display:'block', color:'rgba(255,255,255,0.7)', fontSize:'12px' },
  avatar:      { fontSize:'36px' },
  logoutSmall: { background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', padding:'6px 12px', borderRadius:'8px', cursor:'pointer', fontSize:'13px' },
  statusRow:   { display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', marginBottom:'15px' },
  statusDot:   (on) => ({ width:'10px', height:'10px', borderRadius:'50%', background: on?'#fff':'#888', boxShadow: on?'0 0 0 3px rgba(255,255,255,0.3)':'none' }),
  statusText:  { color:'#fff', fontSize:'14px', fontWeight:'bold' },
  toggleBtn:   { display:'block', margin:'0 auto', padding:'12px 40px', borderRadius:'25px', border:'none', fontSize:'16px', fontWeight:'bold', cursor:'pointer' },
  toggleOn:    { background:'#fff', color:'#11998e' },
  toggleOff:   { background:'rgba(255,255,255,0.2)', color:'#fff' },
  statsRow:    { display:'flex', background:'#fff', borderBottom:'1px solid #eee' },
  stat:        { flex:1, padding:'16px', textAlign:'center', borderLeft:'1px solid #eee' },
  statNum:     { display:'block', fontWeight:'bold', fontSize:'20px', color:'#1a1a2e' },
  statLabel:   { display:'block', fontSize:'12px', color:'#888', marginTop:'2px' },
  tabs:        { display:'flex', background:'#fff', borderBottom:'2px solid #f0f0f0' },
  tab:         { flex:1, padding:'12px', border:'none', background:'none', cursor:'pointer', fontSize:'13px', color:'#888', fontWeight:'bold' },
  tabActive:   { borderBottom:'3px solid #10b981', color:'#10b981' },
  centerMsg:   { textAlign:'center', padding:'40px 20px' },
  activeCard:  { margin:'16px', background:'#fff', borderRadius:'16px', padding:'20px', boxShadow:'0 4px 15px rgba(0,0,0,0.1)' },
  activeTitle: { margin:'0 0 16px', color:'#1a1a2e', fontSize:'18px' },
  activeDetail:{ background:'#f8f9ff', borderRadius:'12px', padding:'14px', marginBottom:'16px' },
  activeRow:   { margin:'8px 0', fontSize:'14px', color:'#555', display:'flex', gap:'8px' },
  actionBtns:  { display:'flex', flexDirection:'column', gap:'10px' },
  actionBtn:   (bg) => ({ padding:'14px', background:bg, color:'#fff', border:'none', borderRadius:'12px', fontSize:'15px', fontWeight:'bold', cursor:'pointer' }),
  orderCard:   { background:'#fff', borderRadius:'12px', padding:'16px', marginBottom:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
  badge:       { padding:'4px 12px', borderRadius:'20px', fontSize:'12px', fontWeight:'bold' },
  offerOverlay:{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'20px' },
  offerCard:   { background:'#fff', borderRadius:'24px', padding:'24px', width:'100%', maxWidth:'380px', textAlign:'center' },
  offerTimer:  (c) => ({ width:'70px', height:'70px', borderRadius:'50%', background: c>15?'#27ae60':c>5?'#f39c12':'#e74c3c', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', transition:'background 0.5s' }),
  offerTimerNum: { color:'#fff', fontWeight:'900', fontSize:'24px', lineHeight:1 },
  offerTimerLabel: { color:'rgba(255,255,255,0.8)', fontSize:'11px' },
  offerTitle:  { fontSize:'20px', fontWeight:'bold', margin:'0 0 16px', color:'#1a1a2e' },
  offerDetail: { background:'#f8f9ff', borderRadius:'12px', padding:'16px', marginBottom:'16px', textAlign:'right' },
  offerRow:    { display:'flex', alignItems:'flex-start', gap:'12px', padding:'6px 0' },
  offerIcon:   { fontSize:'20px', flexShrink:0 },
  offerDLabel: { fontSize:'11px', color:'#888', marginBottom:'2px' },
  offerDVal:   { fontSize:'14px', color:'#333', fontWeight:'bold' },
  offerDivider:{ height:'1px', background:'#eee', margin:'8px 0' },
  offerPrice:  { fontSize:'22px', fontWeight:'bold', color:'#27ae60', margin:'0 0 16px' },
  offerBtns:   { display:'flex', gap:'10px' },
  rejectBtn:   { flex:1, padding:'14px', background:'#ffeaea', color:'#e74c3c', border:'none', borderRadius:'12px', fontSize:'16px', fontWeight:'bold', cursor:'pointer' },
  acceptBtn:   { flex:2, padding:'14px', background:'linear-gradient(135deg,#11998e,#38ef7d)', color:'#fff', border:'none', borderRadius:'12px', fontSize:'16px', fontWeight:'bold', cursor:'pointer' },
  pendingPage: { minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#f0f2f5', direction:'rtl', padding:'20px', textAlign:'center' },
  pendingIcon: { fontSize:'60px', marginBottom:'16px' },
  pendingTitle:{ fontSize:'24px', fontWeight:'bold', color:'#1a1a2e', margin:'0 0 8px' },
  pendingSub:  { color:'#888', fontSize:'15px', margin:'0 0 30px' },
  logoutBtn:   { padding:'12px 30px', background:'#e74c3c', color:'#fff', border:'none', borderRadius:'12px', fontSize:'15px', cursor:'pointer', fontWeight:'bold' },
};
